import {
    AppEvent,
    EventBusInstance,
    isValidString,
    LogLevel,
    SearchQueryBuilder,
    showBanner,
    Sisp,
} from '@sprint/sprint-react-components';
import React, { FormEvent, FunctionComponent, useContext, useEffect, useRef, useState } from 'react';
import { Card, Form, Spinner } from 'react-bootstrap';
import { ProductRequest } from '../../Api/ProductRequest';
import { VatRateRequest } from '../../Api/VatRateRequest';
import { RepositoryFactoryContext } from '../../index';
import Product from '../../Models/Product';
import ProductEditState from '../../Models/ProductEditState';
import VatRate from '../../Models/VatRate';
import './ProductsSisps.scss';

interface Props {
    shown: boolean;
    uniqueKey: string;
    onSuccess: (results: any) => Promise<boolean>;
}

const ProductsEditSisp: FunctionComponent<Props> = (props: Props) => {
    const productRepository = useContext(RepositoryFactoryContext).getApiRepository(new ProductRequest());
    const vatRateRepository = useContext(RepositoryFactoryContext).getApiRepository(new VatRateRequest());

    // State: General
    const [shown, setShown] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState<boolean>(true);

    const focusRef = useRef<HTMLInputElement>(null);

    const [rowId, setRowId] = useState<number>(0);

    const [editProductName, setEditProductName] = useState<string>('');
    const [editProductNameValid, setEditProductNameValid] = useState<boolean>(true);

    const [editProductDescription, setEditProductDescription] = useState<string>('');

    const [editNetPrice, setEditNetPrice] = useState<string>('');

    const [editVatRate, setEditVatRate] = useState<string>('');
    const [vatRates, setVatRates] = useState<any[]>();

    const getVatRates = (): Promise<boolean> => {
        return vatRateRepository
            .search(new SearchQueryBuilder().build())
            .then((results: any) => {
                setVatRates(
                    results.results.map((vatRate: VatRate) => {
                        return vatRate;
                    }),
                );
                setIsLoading(false);
                return true;
            })
            .catch((err: any) => {
                showBanner({
                    message: 'Failed to get Vat Rates - ' + (err?.message ?? err),
                    level: LogLevel.ERROR,
                    dismissable: false,
                });
                return false;
            });
    };

    useEffect(() => {
        EventBusInstance.subscribe('show-hoverover-component', (event: AppEvent<ProductEditState>) => {
            if (event.target !== props.uniqueKey) return;
            setRowId(event.message.id);
            setEditProductName(event.message.product_name);
            setEditProductDescription(event.message.product_description);
            setEditNetPrice(event.message.net_price.replace('£', ''));
            setEditVatRate(event.message.vat_rate.replace('%', ''));
            setShown(true);
        });
        if (shown) {
            setIsLoading(true);
            getVatRates();
            setEditProductNameValid(true);
        }
    }, [shown]);

    useEffect(() => {
        if (!isLoading) {
            if (focusRef.current) {
                focusRef.current.focus();
                focusRef.current.selectionStart = focusRef.current.value.length;
                focusRef.current.selectionEnd = focusRef.current.value.length;
            }
        }
    }, [isLoading]);

    const validate = async (): Promise<boolean> => {
        const productNameValid = !!editProductName && isValidString(editProductName);
        setEditProductNameValid(productNameValid);
        return productNameValid;
    };

    const handleEditRow = async (): Promise<boolean> => {
        const Product: Product = {
            id: rowId,
            product_name: editProductName,
            product_description: editProductDescription,
            net_price: editNetPrice,
            vat_rate: editVatRate,
        };

        return productRepository
            .update(Product)
            .then((results: any) => {
                props.onSuccess(results.data);
                return Promise.resolve(true);
            })
            .catch((err) => {
                showBanner({
                    message: 'Failed to edit product - ' + (err?.message ?? err),
                    level: LogLevel.ERROR,
                });
                return Promise.resolve(false);
            });
    };

    const onSubmitForm = async (e: FormEvent) => {
        e.preventDefault();
        if ((await validate()) && (await handleEditRow())) setShown(false);
    };

    return (
        <Sisp
            isOpen={shown}
            onSubmit={handleEditRow}
            onCancel={() => {
                setShown(false);
            }}
            validate={validate}
        >
            <h4>Edit a Product</h4>
            {isLoading ? (
                <div style={{ position: 'relative', alignItems: 'center' }}>
                    <Card
                        className="loading-spinner-container filter-loading-spinner"
                        style={{ background: '#f9f9f9' }}
                    >
                        <Spinner animation="border" role="status" />
                    </Card>
                </div>
            ) : (
                <Form onSubmit={onSubmitForm}>
                    <Form.Group>
                        <Form.Label>
                            Product <span className="required-field-marker">*</span>
                        </Form.Label>
                        <Form.Control
                            ref={focusRef}
                            type="text"
                            isInvalid={!editProductNameValid}
                            value={editProductName || ''}
                            onChange={(event) => {
                                setEditProductName(event.target.value);
                                setEditProductNameValid(true);
                            }}
                        />
                        <Form.Control.Feedback type="invalid">
                            {!editProductNameValid && 'This field is required.'}
                        </Form.Control.Feedback>
                    </Form.Group>
                    <Form.Group>
                        <Form.Label>Description</Form.Label>
                        <Form.Control
                            type="text"
                            value={editProductDescription}
                            onChange={(event) => {
                                setEditProductDescription(event.target.value);
                            }}
                        />
                    </Form.Group>
                    <Form.Group>
                        <Form.Label>Price (£)</Form.Label>
                        <Form.Control
                            type="text"
                            value={editNetPrice}
                            onChange={(event) => {
                                setEditNetPrice(event.target.value);
                            }}
                        />
                    </Form.Group>
                    <Form.Group>
                        <Form.Label>Tax Rate</Form.Label>
                        {vatRates &&
                            vatRates.map((vatRate) => (
                                <Form.Check
                                    id={`vat-rate-edit-${vatRate.id}`}
                                    name="vat-rate"
                                    type="radio"
                                    key={vatRate.id}
                                    label={`${vatRate.vat_rate}% ${vatRate.description}`}
                                    onChange={() => setEditVatRate(vatRate.vat_rate)}
                                    custom
                                    checked={editVatRate == vatRate.vat_rate}
                                />
                            ))}
                    </Form.Group>
                </Form>
            )}
        </Sisp>
    );
};

export default ProductsEditSisp;
